import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';

const CRIT = ['BANNED', 'FIRE', 'SMOKE', 'RESTRICTED', 'INTRUD', 'WEAPON', 'ARMS', 'GUN', 'FALL', 'SOS', 'PANIC'];
const HIGH = ['UNKNOWN', 'HELMET', 'PPE', 'CROWD', 'AFTER', 'TAMPER', 'ABSEN'];
function severityOf(label = '') {
  const L = label.toUpperCase();
  if (CRIT.some(k => L.includes(k))) return 'critical';
  if (HIGH.some(k => L.includes(k))) return 'high';
  return 'info';
}
function ymd(d) {
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}
function cleanLabel(l = '') { return l.replace(/[^\w\s:.\-/]/g, '').trim() || 'Event'; }

export default function IndustrialReportsPage() {
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const to = new Date(), from = new Date(Date.now() - 7 * 86400 * 1000);
        const r = await apiFetch(`/ui/alerts?page=0&per_page=500&exclude=snapshot&date_from=${ymd(from)}&date_to=${ymd(to)}`);
        const d = await r.json();
        if (live) setAlerts(d.alerts || []);
      } catch {
        if (live) setError('Failed to load report data');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  const byCamera = useMemo(() => {
    const m = new Map();
    alerts.forEach(a => { const k = a.cam_name || 'unknown'; m.set(k, (m.get(k) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [alerts]);

  const byLabel = useMemo(() => {
    const m = new Map();
    alerts.forEach(a => { const k = cleanLabel(a.label); m.set(k, (m.get(k) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [alerts]);

  const bySeverity = useMemo(() => {
    const c = { critical: 0, high: 0, info: 0 };
    alerts.forEach(a => { c[severityOf(a.label)]++; });
    return c;
  }, [alerts]);

  const maxCam = Math.max(1, ...byCamera.map(x => x[1]));
  const maxLab = Math.max(1, ...byLabel.map(x => x[1]));
  const sevTot = Math.max(1, bySeverity.critical + bySeverity.high + bySeverity.info);

  return (
    <IndustrialLayout
      eyebrow="Shift digest"
      title="Reports"
      subtitle={loading ? 'LOADING…' : error ? error : `${alerts.length} EVENTS · LAST 7 DAYS`}
      banner={
        <div className="ind-banner preview">
          Preview — computed live from the alert log. Scheduled email digests and PPE-by-zone breakdowns wire in a later pass.
        </div>
      }>
      {loading ? (
        <div className="ind-empty">Building report…</div>
      ) : (
        <div className="ind-reps">
          <div className="ind-rep">
            <h3>Events by camera</h3>
            <div className="when">LAST 7 DAYS</div>
            {byCamera.length ? byCamera.map(([nm, n]) => (
              <div key={nm} className="ind-brow">
                <span className="nm">{nm}</span>
                <span className="tr"><i style={{ width: `${(n / maxCam) * 100}%` }} /></span>
                <span className="vv">{n}</span>
              </div>
            )) : <div className="ind-empty">No data.</div>}
          </div>

          <div className="ind-rep">
            <h3>Events by type</h3>
            <div className="when">LAST 7 DAYS</div>
            {byLabel.length ? byLabel.map(([nm, n]) => (
              <div key={nm} className="ind-brow">
                <span className="nm">{nm}</span>
                <span className="tr"><i className="y" style={{ width: `${(n / maxLab) * 100}%` }} /></span>
                <span className="vv">{n}</span>
              </div>
            )) : <div className="ind-empty">No data.</div>}
          </div>

          <div className="ind-rep">
            <h3>Severity mix</h3>
            <div className="when">DISPLAY-DERIVED FROM LABELS</div>
            <div className="ind-brow"><span className="nm">Critical</span><span className="tr"><i className="r" style={{ width: `${(bySeverity.critical / sevTot) * 100}%` }} /></span><span className="vv">{bySeverity.critical}</span></div>
            <div className="ind-brow"><span className="nm">High</span><span className="tr"><i className="y" style={{ width: `${(bySeverity.high / sevTot) * 100}%` }} /></span><span className="vv">{bySeverity.high}</span></div>
            <div className="ind-brow"><span className="nm">Info</span><span className="tr"><i style={{ width: `${(bySeverity.info / sevTot) * 100}%` }} /></span><span className="vv">{bySeverity.info}</span></div>
          </div>
        </div>
      )}
    </IndustrialLayout>
  );
}
